import { useState, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import copy from 'copy-to-clipboard';
import isEqual from 'lodash.isequal';
import formatDate from '../../../helpers/format-date';
import SidepanelSelectRow from '../sidepanel-select-row';
import SidepanelDateRow from '../sidepanel-date-row';
import { SelectItemProps } from '../../select/types';
import {
  Account,
  Balance,
  BaseellaStatus,
  Currency,
  ExchangeTransaction,
  ExchangeTypeEnum,
} from '../types';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import SidepanelNumberRow from '../sidepanel-number-row';
import IconRefresh from '../../icons/refresh';
import ButtonPseudo from '../../button/button-pseudo';
import formatBalance from '../../../helpers/format-balance';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import SidepanelTextRow from '../sidepanel-text-row';
import IconLoader from '../../icons/loader';
import usePrevious from '../../../hooks/use-previous';
import AccountsSelector from '../../accounts-selector/accounts-selector';
import { Category } from '../../accounts-selector/types';
import IconSwap from '../../icons/swap';
import SidepanelAmountRow from '../sidepanel-amount-row';
import IconCopy from '../../icons/copy-two';
import IconDown from '../../icons/down';
import ClickOutsideDetector from '../../autocomplete/click-outside-detector';
import DisableCreationOption from '../disable-creation-option/disable-creation-options';

import styles from '../transaction.module.css';

export interface ExchangeGeneralContentProps {
  transactionInfo?: ExchangeTransaction;
  control?: Control;
  currencies?: SelectItemProps[];
  errors?: FieldErrors<ExchangeTransaction>;
  isEditable?: boolean;
  recipientAccounts?: Account[];
  senderAccounts?: Account[];
  onSearchAutocomplete?: (name: string, search: string) => void;
  rateSources?: SelectItemProps[];
  senderAccountsIsLoading?: boolean;
  recipientAccountsIsLoading?: boolean;
  currencyFromId?: string;
  currencyToId?: string;
  fromAccountBalance?: number;
  balanceFromAccountIsLoading?: boolean;
  toAccountBalance?: number;
  balanceToAccountIsLoading?: boolean;
  currencyFromName?: string;
  currencyToName?: string;
  exchangeType?: string;
  setValue?: UseFormSetValue<any>;
  senderAccountNumber?: string;
  calculateResult?: {
    marginPercent: number;
    actualRate: number;
    inverseActualRate: number;
  };
  baseellaStatus?: BaseellaStatus;
  senderAccountName?: string;
  recipientAccountName?: string;
  isLoading?: boolean;
  rateSourceName?: string;
  exchangeCurrencyFixedId?: string;
  accountCategories?: Category[];
  accountsCategoriesLoading?: boolean;
  setSenderAccountQuery?: (query: string) => void;
  setSenderAccounts?: (data: Account[]) => void;
  setRecipientAccountQuery?: (query: string) => void;
  setRecipientAccounts?: (data: Account[]) => void;
  onUpdateRates?: () => void;
  calculateInProgress?: boolean;
  providersRate?: number;
  calcError?: string;
  fromAccountBalances?: Balance[];
  toAccountBalances?: Balance[];
  currenciesList?: Currency[];
  transactionId?: string;
  calculateTransactionRatesByIdAction?: (
    calculateFields: {
      exchangeDateOfRate?: string;
      exchangeRateSourceId?: string;
      exchangeMarginPercent?: number;
      exchangeActualRate?: number;
      inverseActualRate?: number;
    },
    type: 'rate' | 'margin' | 'date' | 'inversed-rate',
  ) => void;
  fields?: any;
  caspEnabled?: boolean;
  overdraftEnabled?: boolean;
  baseRateSource?: string;
  calculateOnSwap?: (data: {
    currencyFrom: string;
    currencyTo: string;
    currencyFixed: string;
  }) => void;
  setInverseRateChanged?: (data: boolean) => void;
  exchangeDateOfRate?: string;
  setError?: UseFormSetError<ExchangeTransaction>;
  clearErrors?: UseFormClearErrors<ExchangeTransaction>;
}

const exchangeTypes = [
  {
    name: 'Sell',
    value: ExchangeTypeEnum.SELL,
  },
  {
    name: 'Buy',
    value: ExchangeTypeEnum.BUY,
  },
];

let fromTimeout: NodeJS.Timeout;
let toTimeout: NodeJS.Timeout;

export default function ExchangeGeneralContent({
  transactionInfo,
  control,
  errors,
  isEditable,
  recipientAccounts,
  senderAccounts,
  onSearchAutocomplete,
  rateSources,
  senderAccountsIsLoading,
  recipientAccountsIsLoading,
  currencyFromId,
  currencyToId,
  fromAccountBalance,
  balanceFromAccountIsLoading,
  toAccountBalance,
  balanceToAccountIsLoading,
  currencyFromName,
  currencyToName,
  exchangeType,
  setValue,
  senderAccountNumber,
  calculateResult,
  baseellaStatus,
  senderAccountName,
  recipientAccountName,
  isLoading,
  rateSourceName,
  accountsCategoriesLoading,
  accountCategories,
  setSenderAccountQuery,
  setSenderAccounts,
  setRecipientAccountQuery,
  setRecipientAccounts,
  onUpdateRates,
  calculateInProgress,
  exchangeCurrencyFixedId,
  providersRate,
  calcError,
  fromAccountBalances,
  toAccountBalances,
  currencies,
  currenciesList,
  transactionId,
  fields,
  calculateTransactionRatesByIdAction,
  caspEnabled,
  overdraftEnabled,
  baseRateSource,
  calculateOnSwap,
  setInverseRateChanged,
  exchangeDateOfRate,
  setError,
  clearErrors,
}: ExchangeGeneralContentProps) {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const enableEdit = Boolean(control);

  const [sumValue, setSumValue] = useState('');
  const [sellSumValue, setSellSumValue] = useState('');
  const [buySumValue, setBuySumValue] = useState('');
  const [marginValue, setMarginValue] = useState('');
  const [rateValue, setRateValue] = useState('');
  const [inversedRateValue, setInversedRateValue] = useState('');
  const [currenciesFrom, setCurrenciesFrom] = useState<SelectItemProps[]>([]);
  const [currenciesTo, setCurrenciesTo] = useState<SelectItemProps[]>([]);
  const [balanceFromValueVisible, setBalanceFromValueVisible] = useState(false);
  const [maxValueFromWidth, setMaxValueFromWidth] = useState(0);
  const [balanceToValueVisible, setBalanceToValueVisible] = useState(false);
  const [maxValueToWidth, setMaxValueToWidth] = useState(0);
  const [balanceFromDetailsOnFocus, setBalanceFromDetailsOnFocus] =
    useState(false);
  const [balanceToDetailsOnFocus, setBalanceToDetailsOnFocus] = useState(false);
  const [currenFromAccount, setCurrentFromAccount] = useState<Account>();
  const [currenToAccount, setCurrentToAccount] = useState<Account>();
  const [currentFromBalance, setCurrentFromBalance] = useState<Balance>();
  const [currentToBalance, setCurrentToBalance] = useState<Balance>();

  let rateSourceValue = '';
  let sellAmount;
  let buyAmount;
  let currencySellName;
  let currencyBuyName;
  let senderAccountValue;
  let recipientAccountValue;
  let exchangeTypeValue;

  if (enableEdit) {
    rateSourceValue = transactionInfo?.exchangeRateSourceId as string;
    currencySellName = transactionInfo?.exchangeCurrencyFromId as string;
    currencyBuyName = transactionInfo?.exchangeCurrencyToId as string;
    senderAccountValue = transactionInfo?.senderAccountNumber as string;
    recipientAccountValue = transactionInfo?.recipientAccountNumber as string;
    exchangeTypeValue = transactionInfo?.exchangeType as string;
  } else {
    rateSourceValue =
      (rateSources?.find(
        (item) => item.value === transactionInfo?.exchangeRateSourceId,
      )?.name as string) || (rateSourceName as string);
    currencySellName = currencyFromName as string;
    currencyBuyName = currencyToName as string;
    senderAccountValue = senderAccountName as string;
    recipientAccountValue = recipientAccountName as string;
    exchangeTypeValue = exchangeTypes.find(
      (item) => item.value === transactionInfo?.exchangeType,
    )?.name as string;
  }

  if (exchangeType === ExchangeTypeEnum.SELL) {
    sellAmount = transactionInfo?.sum;
    buyAmount = transactionInfo?.calculatedAmount;
  } else {
    sellAmount = transactionInfo?.calculatedAmount;
    buyAmount = transactionInfo?.sum;
  }

  const prevCalculateResult = usePrevious(calculateResult);

  useEffect(() => {
    if (fromAccountBalances && currencies && !transactionInfo) {
      const nextCurrencies: SelectItemProps[] = [];

      currencies.forEach((item) => {
        if (
          fromAccountBalances?.find(
            (balance) => balance.currencyId === item.value,
          )
        ) {
          nextCurrencies.push(item);
        }
      });

      setCurrenciesFrom(nextCurrencies);
    }
  }, [fromAccountBalances, currencies, transactionInfo]);

  useEffect(() => {
    if (toAccountBalances && currencies && !transactionInfo) {
      const nextCurrencies: SelectItemProps[] = [];

      currencies.forEach((item) => {
        if (
          toAccountBalances?.find(
            (balance) => balance.currencyId === item.value,
          )
        ) {
          nextCurrencies.push(item);
        }
      });

      setCurrenciesTo(nextCurrencies);
    }
  }, [toAccountBalances, currencies, transactionInfo]);

  useEffect(() => {
    if (transactionInfo) {
      setCurrenciesFrom([
        {
          name: transactionInfo?.exchangeCurrencyFrom?.name as string,
          value: transactionInfo?.exchangeCurrencyFrom?.id as string,
        },
      ]);

      setCurrentFromAccount(
        senderAccounts?.find(
          (item) => item.fullNumber === transactionInfo?.senderAccountNumber,
        ),
      );

      setCurrenciesTo([
        {
          name: transactionInfo?.exchangeCurrencyTo?.name as string,
          value: transactionInfo?.exchangeCurrencyTo?.id as string,
        },
      ]);

      setCurrentToAccount(
        recipientAccounts?.find(
          (item) => item.fullNumber === transactionInfo?.recipientAccountNumber,
        ),
      );
    }
  }, [transactionInfo, senderAccounts, recipientAccounts]);

  useEffect(() => {
    if (!transactionInfo && senderAccounts && fields.senderAccountNumber) {
      setCurrentFromAccount(
        senderAccounts?.find(
          (item) => item.fullNumber === fields?.senderAccountNumber,
        ),
      );
    }
  }, [transactionInfo, senderAccounts, fields]);

  useEffect(() => {
    if (
      !transactionInfo &&
      recipientAccounts &&
      fields.recipientAccountNumber
    ) {
      setCurrentToAccount(
        recipientAccounts?.find(
          (item) => item.fullNumber === fields?.recipientAccountNumber,
        ),
      );
    }
  }, [transactionInfo, recipientAccounts, fields]);

  useEffect(() => {
    setCurrentFromBalance(
      fromAccountBalances?.find(
        (item) =>
          item.currencyId === transactionInfo?.exchangeCurrencyFromId ||
          item.currencyId === currencyFromId,
      ),
    );

    setCurrentToBalance(
      toAccountBalances?.find(
        (item) =>
          item.currencyId === transactionInfo?.exchangeCurrencyToId ||
          item.currencyId === currencyToId,
      ),
    );
  }, [
    transactionInfo,
    fromAccountBalances,
    toAccountBalances,
    fields,
    currencyFromId,
    currencyToId,
  ]);

  useEffect(() => {
    if (calcError) {
      setMarginValue('');
      setRateValue('');
      setInversedRateValue('');
    }
  }, [calcError]);

  useEffect(() => {
    if (
      calculateResult &&
      !isEqual(calculateResult, prevCalculateResult) &&
      Number(rateValue) !== calculateResult.actualRate
    ) {
      setRateValue(calculateResult?.actualRate?.toString());
    }
  }, [calculateResult, rateValue, prevCalculateResult]);

  useEffect(() => {
    if (
      calculateResult &&
      !isEqual(calculateResult, prevCalculateResult) &&
      inversedRateValue &&
      Number(inversedRateValue) !== calculateResult.inverseActualRate
    ) {
      setInversedRateValue(calculateResult?.inverseActualRate?.toString());
    }
  }, [
    calculateResult,
    inversedRateValue,
    prevCalculateResult,
    inversedRateValue,
  ]);

  useEffect(() => {
    if (balanceFromValueVisible && !balanceFromDetailsOnFocus) {
      fromTimeout = setTimeout(() => {
        setBalanceFromValueVisible(false);
        setMaxValueFromWidth(0);
      }, 3000);
    }

    if (balanceFromDetailsOnFocus) {
      clearTimeout(fromTimeout);
    }
  }, [balanceFromDetailsOnFocus, balanceFromValueVisible]);

  useEffect(() => {
    if (balanceToValueVisible && !balanceToDetailsOnFocus) {
      toTimeout = setTimeout(() => {
        setBalanceToValueVisible(false);
        setMaxValueToWidth(0);
      }, 3000);
    }

    if (balanceToDetailsOnFocus) {
      clearTimeout(toTimeout);
    }
  }, [balanceToDetailsOnFocus, balanceToValueVisible]);

  useEffect(() => {
    if (
      calculateResult &&
      !isEqual(calculateResult, prevCalculateResult) &&
      Number(marginValue) !== calculateResult?.marginPercent
    ) {
      setMarginValue(calculateResult?.marginPercent?.toString());
    }
  }, [calculateResult, marginValue, prevCalculateResult]);

  let content;

  useEffect(() => {
    if (balanceFromValueVisible) {
      const valuesFrom = document.querySelectorAll('.value-from');

      if (valuesFrom && valuesFrom.length) {
        const valuesWidth: number[] = [];

        valuesFrom?.forEach((item) => {
          valuesWidth.push(item.clientWidth);
        });

        setMaxValueFromWidth(Math.max.apply(null, valuesWidth));
      }
    }
  }, [balanceFromValueVisible]);

  useEffect(() => {
    if (balanceToValueVisible) {
      const valuesTo = document.querySelectorAll('.value-to');

      if (valuesTo && valuesTo.length) {
        const valuesWidth: number[] = [];

        valuesTo?.forEach((item) => {
          valuesWidth.push(item.clientWidth);
        });

        setMaxValueToWidth(Math.max.apply(null, valuesWidth));
      }
    }
  }, [balanceToValueVisible]);

  let sellSumName = '';
  let buySumName = '';
  let sellSum;
  let buySum;

  if (!transactionInfo) {
    sellSumName = 'sellSum';
    buySumName = 'buySum';
    sellSum = sellSumValue;
    buySum = buySumValue;
  } else if (transactionInfo) {
    sellSumName =
      exchangeType === ExchangeTypeEnum.SELL ? 'sum' : 'calculatedAmount';
    sellSum = exchangeType === ExchangeTypeEnum.SELL ? sumValue : undefined;

    buySumName =
      exchangeType === ExchangeTypeEnum.BUY ? 'sum' : 'calculatedAmount';
    buySum = exchangeType === ExchangeTypeEnum.BUY ? sumValue : undefined;
  }

  useEffect(() => {
    if (!transactionInfo) {
      if (
        exchangeType &&
        sellSumValue &&
        exchangeType === 'BUY' &&
        fields.sellSum !== sellSumValue
      ) {
        setSellSumValue(fields.sellSum);
      }
    }
  }, [fields, exchangeType, sellSumValue]);

  useEffect(() => {
    if (!transactionInfo) {
      if (
        exchangeType &&
        buySumValue &&
        exchangeType === 'SELL' &&
        fields.buySum !== buySumValue
      ) {
        setBuySumValue(fields.buySum);
      }
    }
  }, [fields, exchangeType, sellSumValue]);

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    content = (
      <>
        <div className={styles.transactionBlockWrapper}>
          <div
            className={`${styles.amountRow} ${
              !control || !transactionInfo ? styles.noFlex : ''
            } ${styles.dateRowWrapper}`}
          >
            {transactionInfo && (
              <div
                className={`${styles.amountRowItem} ${styles.dateOfRateWrapper}`}
              >
                <SidepanelDateRow
                  title="Creation Date"
                  value={formatDate(transactionInfo?.createdAt || '')}
                  name="createdAt"
                  placeholder="Select Date"
                  control={control}
                  enableEdit={enableEdit}
                  error={errors?.createdAt?.message}
                  tabIndex={-1}
                  copyEnabled={Boolean(transactionInfo)}
                  copyValue={formatDate(fields?.createdAt || '')}
                  disabled
                />
              </div>
            )}
            <div
              className={`${styles.amountRowItem} ${styles.dateOfRateWrapper}`}
            >
              <SidepanelDateRow
                title="Exchange date"
                value={
                  enableEdit
                    ? ''
                    : formatDate(transactionInfo?.transferDate || '')
                }
                name="transferDate"
                placeholder="Select Date"
                control={control}
                enableEdit={enableEdit}
                error={errors?.transferDate?.message}
                disabled={!isEditable || Boolean(transactionInfo)}
                tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={formatDate(fields?.transferDate || '')}
              />
            </div>
          </div>

          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div
              className={`${styles.amountRowItem} ${styles.dateOfRateWrapper}`}
            >
              <SidepanelSelectRow
                title="Rate source"
                value={rateSourceValue}
                name="exchangeRateSourceId"
                placeholder="Choose rate source"
                control={control}
                selectList={rateSources}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionInfo)}
                tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
                error={errors?.exchangeRateSourceId?.message}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  rateSources?.find(
                    (item) => item.value === fields?.exchangeRateSourceId,
                  )?.name
                }
              />
            </div>
            <div
              className={`${styles.amountRowItem} ${styles.dateOfRateWrapper}`}
            >
              <SidepanelDateRow
                title="Date of the Rate"
                value={
                  enableEdit
                    ? ''
                    : formatDate(transactionInfo?.exchangeDateOfRate || '')
                }
                name="exchangeDateOfRate"
                placeholder="Select Date"
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED
                }
                tabIndex={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED
                    ? -1
                    : 0
                }
                error={errors?.exchangeDateOfRate?.message}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={formatDate(fields?.exchangeDateOfRate || '')}
                withTime
              />
            </div>
          </div>
          <div className={styles.depositeDateWrapper}>
            <SidepanelDateRow
              title="Transaction Posting date"
              value={
                enableEdit
                  ? ''
                  : formatDate(
                      transactionInfo?.depositDateOfDestinationAccount || '',
                    )
              }
              name="depositDateOfDestinationAccount"
              placeholder="Select Transaction Posting date"
              control={control}
              enableEdit={enableEdit}
              tabIndex={
                !isEditable || !exchangeDateOfRate || Boolean(transactionInfo)
                  ? -1
                  : 0
              }
              disabled={
                !isEditable || !exchangeDateOfRate || Boolean(transactionInfo)
              }
              error={errors?.depositDateOfDestinationAccount?.message}
              copyEnabled={Boolean(transactionInfo)}
              copyValue={formatDate(
                fields?.depositDateOfDestinationAccount || '',
              )}
            />
          </div>
        </div>
        <div className={styles.transactionBlockWrapper}>
          {onSearchAutocomplete &&
          setSenderAccountQuery &&
          setSenderAccounts ? (
            <SidepanelDefaultRow
              valueClassName={transactionInfo ? styles.rowWithCopy : ''}
            >
              <Controller
                control={control}
                name="senderAccountNumber"
                render={({ field: { value, name, onChange } }) => (
                  <AccountsSelector
                    title="From Account"
                    placeholder="Enter name or number..."
                    className={styles.selectAccount}
                    accounts={accountCategories}
                    isLoading={accountsCategoriesLoading}
                    selectedAccount={value}
                    accountsIsLoading={senderAccountsIsLoading}
                    onSearch={(searchText) => {
                      onSearchAutocomplete(name, searchText);
                    }}
                    setAccountQuery={setSenderAccountQuery}
                    accountsList={senderAccounts || []}
                    setAccounts={setSenderAccounts}
                    setSelectedAccount={(number) => onChange(number)}
                    fullWidth
                    disabled={!isEditable || Boolean(transactionInfo)}
                    tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
                    error={errors?.senderAccountNumber?.message}
                    transactionAccountSelector={
                      !isEditable || Boolean(transactionInfo)
                    }
                  />
                )}
              />
              {Boolean(transactionInfo) && (
                <button
                  type="button"
                  className={`${styles.copyButton} ${
                    styles.autocompleteCopyButton
                  } ${copyIsSuccess ? styles.success : ''}`}
                  onClick={() =>
                    onCopy(
                      senderAccounts?.find(
                        (item) =>
                          item.fullNumber === fields?.senderAccountNumber,
                      )?.fullNumber || '-',
                    )
                  }
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              )}
            </SidepanelDefaultRow>
          ) : (
            <SidepanelAutocompleteRow
              title="From Account"
              value={senderAccountValue}
              name="senderAccountNumber"
              placeholder="Choose Account"
              autocompleteList={senderAccounts?.map((item) => ({
                name: item.description,
                value: item.fullNumber,
                suffix: item.fullNumber,
              }))}
              isLoading={senderAccountsIsLoading}
              control={control}
              enableEdit={enableEdit}
              disabled={!isEditable || Boolean(transactionInfo)}
              tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
              error={errors?.senderAccountNumber?.message}
              copyEnabled={Boolean(transactionInfo)}
              copyValue={
                senderAccounts?.find(
                  (item) => item.fullNumber === fields?.senderAccountNumber,
                )?.description
              }
              withSuffix
            />
          )}
          {onSearchAutocomplete &&
          setRecipientAccountQuery &&
          setRecipientAccounts ? (
            <SidepanelDefaultRow
              valueClassName={transactionInfo ? styles.rowWithCopy : ''}
            >
              <Controller
                control={control}
                name="recipientAccountNumber"
                render={({ field: { value, name, onChange } }) => (
                  <AccountsSelector
                    title="To Account"
                    placeholder="Enter name or number..."
                    className={`${styles.selectAccount} ${styles.recipientAccount}`}
                    accounts={accountCategories}
                    isLoading={accountsCategoriesLoading}
                    selectedAccount={value}
                    accountsIsLoading={recipientAccountsIsLoading}
                    onSearch={(searchText) => {
                      onSearchAutocomplete(name, searchText);
                    }}
                    setAccountQuery={setRecipientAccountQuery}
                    accountsList={recipientAccounts || []}
                    setAccounts={setRecipientAccounts}
                    setSelectedAccount={(number) => onChange(number)}
                    fullWidth
                    disabled={
                      !isEditable ||
                      Boolean(transactionInfo) ||
                      !senderAccountNumber
                    }
                    tabIndex={
                      !isEditable ||
                      Boolean(transactionInfo) ||
                      !senderAccountNumber
                        ? -1
                        : 0
                    }
                    error={errors?.recipientAccountNumber?.message}
                    transactionAccountSelector={
                      !isEditable || Boolean(transactionInfo)
                    }
                  />
                )}
              />
              {Boolean(transactionInfo) && (
                <button
                  type="button"
                  className={`${styles.copyButton} ${
                    styles.autocompleteCopyButton
                  } ${copyIsSuccess ? styles.success : ''}`}
                  onClick={() =>
                    onCopy(
                      recipientAccounts?.find(
                        (item) =>
                          item.fullNumber === fields?.recipientAccountNumber,
                      )?.fullNumber || '-',
                    )
                  }
                  aria-label="Copy"
                >
                  <IconCopy />
                </button>
              )}
            </SidepanelDefaultRow>
          ) : (
            <SidepanelAutocompleteRow
              title="To Account"
              value={recipientAccountValue}
              name="recipientAccountNumber"
              placeholder="Choose Account"
              autocompleteList={recipientAccounts?.map((item) => ({
                name: item.description,
                value: item.fullNumber,
                suffix: item.fullNumber,
              }))}
              isLoading={senderAccountsIsLoading}
              control={control}
              enableEdit={enableEdit}
              disabled={
                !isEditable || Boolean(transactionInfo) || !senderAccountNumber
              }
              tabIndex={
                !isEditable || Boolean(transactionInfo) || !senderAccountNumber
                  ? -1
                  : 0
              }
              error={errors?.recipientAccountNumber?.message}
              copyEnabled={Boolean(transactionInfo)}
              copyValue={
                recipientAccounts?.find(
                  (item) => item.fullNumber === fields?.recipientAccountNumber,
                )?.description
              }
              withSuffix
            />
          )}
        </div>
        <div className={styles.transactionBlockWrapper}>
          <SidepanelSelectRow
            title="Exchange Type"
            value={exchangeTypeValue}
            name="exchangeType"
            placeholder="Choose Exchange Type"
            control={control}
            selectList={exchangeTypes}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionInfo)}
            tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
            error={errors?.exchangeType?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              exchangeTypes?.find((item) => item.value === fields?.exchangeType)
                ?.name
            }
          />
          <div
            className={`${styles.exhangeCurrenciesWrapper} ${
              !control ? styles.noFlex : ''
            }`}
          >
            <div className={styles.exchangeCurrency}>
              <SidepanelAutocompleteRow
                title="Sell"
                value={
                  control
                    ? currencySellName
                    : `${formatBalance(Number(sellAmount))} ${currencySellName}`
                }
                name="exchangeCurrencyFromId"
                placeholder="Choose currency"
                autocompleteList={currenciesFrom}
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  Boolean(transactionInfo) ||
                  (!fromAccountBalances && !toAccountBalances) ||
                  !currenciesFrom?.length
                }
                tabIndex={
                  !isEditable ||
                  Boolean(transactionInfo) ||
                  (!fromAccountBalances && !toAccountBalances) ||
                  !currenciesFrom?.length
                    ? -1
                    : 0
                }
                setValue={setValue}
                isFixedExchangeCurrency={exchangeType === ExchangeTypeEnum.SELL}
                error={errors?.exchangeCurrencyFromId?.message}
                className={styles.smallMargin}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? currenciesFrom?.find(
                        (item) => item.value === fields?.exchangeCurrencyFromId,
                      )?.name
                    : currencySellName
                }
                withClearButton
                swap
              />
              {control && (
                <SidepanelAmountRow
                  title=""
                  value={sellAmount}
                  name={sellSumName}
                  placeholder="Enter amount"
                  control={control}
                  enableEdit={enableEdit}
                  fieldValue={sellSum}
                  setFieldValue={
                    // eslint-disable-next-line no-nested-ternary
                    !transactionInfo
                      ? setSellSumValue
                      : exchangeType === ExchangeTypeEnum.SELL
                        ? setSumValue
                        : undefined
                  }
                  isFixedExchangeAmount={exchangeType === ExchangeTypeEnum.SELL}
                  disabled={
                    !isEditable ||
                    Boolean(transactionInfo) ||
                    exchangeType === ExchangeTypeEnum.BUY ||
                    !exchangeType ||
                    (!fromAccountBalances && !toAccountBalances) ||
                    !currenciesFrom?.length
                  }
                  copyEnabled={Boolean(transactionInfo)}
                  copyValue={
                    exchangeType === ExchangeTypeEnum.SELL
                      ? fields?.sum
                      : fields.calculatedAmount
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(transactionInfo) ||
                    exchangeType === ExchangeTypeEnum.BUY ||
                    !exchangeType ||
                    (!fromAccountBalances && !toAccountBalances) ||
                    !currenciesFrom?.length
                      ? -1
                      : 0
                  }
                  error={
                    exchangeType === ExchangeTypeEnum.SELL
                      ? errors?.sum?.message
                      : ''
                  }
                  className={styles.smallMargin}
                  currenciesList={currenciesList}
                  currencyId={currencyFromId}
                />
              )}
              {!control && (
                <>
                  {currenFromAccount &&
                    currentFromBalance &&
                    (!overdraftEnabled ||
                      !currenFromAccount?.overdraftEnabled ||
                      !currentFromBalance?.overdraftEnabled) && (
                      <SidepanelDefaultRow
                        title="Current balance"
                        valueClassName={`${
                          transactionInfo ? styles.readCopyButtonWrapper : ''
                        } ${styles.withMarginBottom}`}
                      >
                        {fromAccountBalance !== undefined
                          ? `${formatBalance(
                              fromAccountBalance,
                              currenciesList?.find(
                                (item) => item.id === currencyFromId,
                              ),
                            )} ${currencySellName}`
                          : '-'}
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              fromAccountBalance !== undefined
                                ? fromAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      </SidepanelDefaultRow>
                    )}
                  {overdraftEnabled &&
                    currenFromAccount?.overdraftEnabled &&
                    currentFromBalance?.overdraftEnabled && (
                      <>
                        <SidepanelDefaultRow
                          title="Current balance"
                          valueClassName={`${
                            transactionInfo ? styles.readCopyButtonWrapper : ''
                          } ${styles.withMarginBottom}`}
                        >
                          {currentFromBalance &&
                          currentFromBalance?.balanceWithOverdraft
                            ? `${formatBalance(
                                currentFromBalance?.balanceWithOverdraft,
                                currenciesList?.find(
                                  (item) => item.id === currencyFromId,
                                ),
                              )} ${currencySellName}`
                            : '-'}
                          <button
                            type="button"
                            className={`${styles.readCopyButton} ${
                              copyIsSuccess ? styles.success : ''
                            }`}
                            onClick={() =>
                              onCopy(
                                currentFromBalance &&
                                  currentFromBalance?.balanceWithOverdraft
                                  ? currentFromBalance?.balanceWithOverdraft?.toString()
                                  : '-',
                              )
                            }
                            aria-label="Copy"
                          >
                            <IconCopy />
                          </button>
                        </SidepanelDefaultRow>
                        <SidepanelDefaultRow title="Own funds">
                          {currentFromBalance
                            ? `${formatBalance(
                                currentFromBalance?.value,
                                currenciesList?.find(
                                  (item) => item.id === currencyFromId,
                                ),
                              )} ${currencySellName}`
                            : '-'}
                        </SidepanelDefaultRow>
                        <SidepanelDefaultRow title="Remaining Overdraft">
                          {formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            currentFromBalance?.value < 0
                              ? (currentFromBalance?.overdraftValue as number) -
                                  Math.abs(currentFromBalance?.value) <
                                0
                                ? 0
                                : (currentFromBalance?.overdraftValue as number) -
                                  Math.abs(currentFromBalance?.value)
                              : (currentFromBalance?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyFromId,
                            ),
                          )}
                        </SidepanelDefaultRow>
                        <SidepanelDefaultRow title="Overdraft Limit">
                          {formatBalance(
                            currentFromBalance?.overdraftValue || 0,
                            currenciesList?.find(
                              (item) => item.id === currencyFromId,
                            ),
                          )}
                        </SidepanelDefaultRow>
                      </>
                    )}
                </>
              )}
              {control && (
                <>
                  {balanceFromAccountIsLoading && !currentFromBalance && (
                    <IconLoader
                      width={12}
                      height={12}
                      className={styles.balanceLoader}
                    />
                  )}
                  {currenFromAccount &&
                    currentFromBalance &&
                    (!overdraftEnabled ||
                      !currenFromAccount?.overdraftEnabled ||
                      !currentFromBalance?.overdraftEnabled) && (
                      <div className={styles.currentCurrencyBalance}>
                        <span>Current balance:</span>
                        <span>
                          {!balanceFromAccountIsLoading &&
                          fromAccountBalance !== undefined
                            ? `${formatBalance(
                                fromAccountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyFromId,
                                ),
                              )} ${
                                currenciesFrom?.find(
                                  (item) => item.value === currencyFromId,
                                )?.name
                              }`
                            : '-'}
                        </span>
                      </div>
                    )}
                  {overdraftEnabled &&
                    currenFromAccount?.overdraftEnabled &&
                    currentFromBalance?.overdraftEnabled && (
                      <div className={styles.exchangeBalance}>
                        <div className={styles.balanceTitle}>
                          Available Funds
                        </div>
                        <ClickOutsideDetector
                          setOptionsListVisible={() => {
                            setBalanceFromValueVisible(false);
                            setMaxValueFromWidth(0);
                            setBalanceFromDetailsOnFocus(false);
                          }}
                        >
                          <span
                            className={styles.balanceValue}
                            onMouseEnter={() =>
                              setBalanceFromValueVisible(true)
                            }
                          >
                            {!balanceFromAccountIsLoading && currentFromBalance
                              ? formatBalance(
                                  currentFromBalance?.balanceWithOverdraft || 0,
                                  currenciesList?.find(
                                    (item) => item.id === currencyFromId,
                                  ),
                                )
                              : ''}
                            {balanceFromAccountIsLoading && (
                              <IconLoader
                                width={12}
                                height={12}
                                className={styles.overdraftBalanceLoader}
                              />
                            )}
                            {!balanceFromAccountIsLoading && <IconDown />}
                            {!balanceFromAccountIsLoading &&
                              currentFromBalance &&
                              balanceFromValueVisible && (
                                <div
                                  className={styles.balanceDetails}
                                  onMouseEnter={() =>
                                    setBalanceFromDetailsOnFocus(true)
                                  }
                                  onMouseLeave={() => {
                                    setBalanceFromValueVisible(false);
                                    setMaxValueFromWidth(0);
                                    setBalanceFromDetailsOnFocus(false);
                                  }}
                                >
                                  <div className={styles.balanceDetailsValue}>
                                    <button
                                      type="button"
                                      className={`${styles.value} value-from`}
                                      style={{
                                        width: maxValueFromWidth
                                          ? `${maxValueFromWidth}px`
                                          : 'auto',
                                      }}
                                      onClick={() => {
                                        if (
                                          currentFromBalance?.value > 0 &&
                                          !transactionInfo
                                        ) {
                                          if (
                                            exchangeType ===
                                            ExchangeTypeEnum.SELL
                                          ) {
                                            setSumValue(
                                              currentFromBalance?.value?.toString(),
                                            );

                                            if (setValue) {
                                              setValue(
                                                'sum',
                                                currentFromBalance?.value?.toString(),
                                              );
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      {formatBalance(
                                        currentFromBalance?.value || 0,
                                        currenciesList?.find(
                                          (item) => item.id === currencyFromId,
                                        ),
                                      )}
                                    </button>
                                    <div className={styles.title}>
                                      Own Funds
                                    </div>
                                  </div>
                                  <div className={styles.balanceDetailsValue}>
                                    <button
                                      type="button"
                                      className={`${styles.value} value-from`}
                                      style={{
                                        width: maxValueFromWidth
                                          ? `${maxValueFromWidth}px`
                                          : 'auto',
                                      }}
                                      onClick={() => {
                                        if (
                                          currentFromBalance?.overdraftValue &&
                                          currentFromBalance?.overdraftValue >
                                            0 &&
                                          !transactionInfo
                                        ) {
                                          if (
                                            exchangeType ===
                                            ExchangeTypeEnum.SELL
                                          ) {
                                            setSumValue(
                                              `${
                                                // eslint-disable-next-line no-nested-ternary
                                                currentFromBalance?.value < 0
                                                  ? (currentFromBalance?.overdraftValue ||
                                                      0) -
                                                      Math.abs(
                                                        currentFromBalance?.value,
                                                      ) <
                                                    0
                                                    ? 0
                                                    : (currentFromBalance?.overdraftValue ||
                                                        0) -
                                                      Math.abs(
                                                        currentFromBalance?.value,
                                                      )
                                                  : currentFromBalance?.overdraftValue
                                              }`,
                                            );

                                            if (setValue) {
                                              setValue(
                                                'sum',
                                                // eslint-disable-next-line no-nested-ternary
                                                currentFromBalance?.value < 0
                                                  ? (currentFromBalance?.overdraftValue ||
                                                      0) -
                                                      Math.abs(
                                                        currentFromBalance?.value,
                                                      ) <
                                                    0
                                                    ? 0
                                                    : (currentFromBalance?.overdraftValue ||
                                                        0) -
                                                      Math.abs(
                                                        currentFromBalance?.value,
                                                      )
                                                  : currentFromBalance?.overdraftValue,
                                              );
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      {formatBalance(
                                        // eslint-disable-next-line no-nested-ternary
                                        currentFromBalance?.value < 0
                                          ? (currentFromBalance?.overdraftValue as number) -
                                              Math.abs(
                                                currentFromBalance?.value,
                                              ) <
                                            0
                                            ? 0
                                            : (currentFromBalance?.overdraftValue as number) -
                                              Math.abs(
                                                currentFromBalance?.value,
                                              )
                                          : (currentFromBalance?.overdraftValue as number),
                                        currenciesList?.find(
                                          (item) => item.id === currencyFromId,
                                        ),
                                      )}
                                    </button>
                                    <div className={styles.title}>
                                      Remaining Overdraft
                                    </div>
                                  </div>
                                  <div className={styles.balanceDetailsValue}>
                                    <div
                                      className={`${styles.value} value-from`}
                                      style={{
                                        width: maxValueFromWidth
                                          ? `${maxValueFromWidth}px`
                                          : 'auto',
                                      }}
                                    >
                                      {formatBalance(
                                        currentFromBalance?.overdraftValue || 0,
                                        currenciesList?.find(
                                          (item) => item.id === currencyFromId,
                                        ),
                                      )}
                                    </div>
                                    <div className={styles.title}>
                                      Overdraft Limit
                                    </div>
                                  </div>
                                </div>
                              )}
                          </span>
                        </ClickOutsideDetector>
                      </div>
                    )}
                </>
              )}
            </div>
            {control && (
              <div className={styles.swapButtonWrapper}>
                <button
                  type="button"
                  className={styles.swapButton}
                  disabled={
                    Boolean(transactionInfo) ||
                    Boolean(!currencyFromId || !currencyToId) ||
                    !currenciesFrom.find(
                      (item) => item.value === currencyToId,
                    ) ||
                    !currenciesTo.find((item) => item.value === currencyFromId)
                  }
                  onClick={() => {
                    if (setValue) {
                      setValue('exchangeCurrencyFromId', currencyToId);
                      setValue('exchangeCurrencyToId', currencyFromId);

                      if (currencyFromId === exchangeCurrencyFixedId) {
                        setValue('exchangeCurrencyFixedId', currencyToId);
                      } else {
                        setValue('exchangeCurrencyFixedId', currencyFromId);
                      }

                      if (
                        calculateResult &&
                        calculateOnSwap &&
                        currencyToId &&
                        currencyFromId
                      ) {
                        calculateOnSwap({
                          currencyFrom: currencyToId,
                          currencyTo: currencyFromId,
                          currencyFixed:
                            currencyFromId === exchangeCurrencyFixedId
                              ? currencyToId
                              : currencyFromId,
                        });
                      }

                      setCurrenciesFrom(currenciesTo);
                      setCurrenciesTo(currenciesFrom);
                    }
                  }}
                  aria-label="Swap"
                >
                  <IconSwap width={25} height={25} />
                </button>
              </div>
            )}
            <div className={styles.exchangeCurrency}>
              <SidepanelAutocompleteRow
                title="Buy"
                value={
                  control
                    ? currencyBuyName
                    : `${formatBalance(Number(buyAmount))} ${currencyBuyName}`
                }
                name="exchangeCurrencyToId"
                placeholder="Choose currency"
                autocompleteList={currenciesTo}
                control={control}
                enableEdit={enableEdit}
                isFixedExchangeCurrency={exchangeType === ExchangeTypeEnum.BUY}
                disabled={
                  !isEditable ||
                  Boolean(transactionInfo) ||
                  (!fromAccountBalances && !toAccountBalances) ||
                  !currenciesTo?.length
                }
                tabIndex={
                  !isEditable ||
                  Boolean(transactionInfo) ||
                  (!fromAccountBalances && !toAccountBalances) ||
                  !currenciesTo?.length
                    ? -1
                    : 0
                }
                error={errors?.exchangeCurrencyToId?.message}
                setValue={setValue}
                className={styles.smallMargin}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? currenciesTo?.find(
                        (item) => item.value === fields?.exchangeCurrencyToId,
                      )?.name
                    : currencyBuyName
                }
                withClearButton
                swap
              />
              {control && (
                <SidepanelAmountRow
                  title=""
                  value={buyAmount}
                  name={buySumName}
                  placeholder="Enter amount"
                  control={control}
                  enableEdit={enableEdit}
                  isFixedExchangeAmount={exchangeType === ExchangeTypeEnum.BUY}
                  fieldValue={buySum}
                  setFieldValue={
                    // eslint-disable-next-line no-nested-ternary
                    !transactionInfo
                      ? setBuySumValue
                      : exchangeType === ExchangeTypeEnum.BUY
                        ? setSumValue
                        : undefined
                  }
                  disabled={
                    !isEditable ||
                    Boolean(transactionInfo) ||
                    exchangeType === ExchangeTypeEnum.SELL ||
                    !exchangeType ||
                    (!fromAccountBalances && !toAccountBalances) ||
                    !currenciesTo?.length
                  }
                  tabIndex={
                    !isEditable ||
                    Boolean(transactionInfo) ||
                    exchangeType === ExchangeTypeEnum.SELL ||
                    !exchangeType ||
                    (!fromAccountBalances && !toAccountBalances) ||
                    !currenciesTo?.length
                      ? -1
                      : 0
                  }
                  copyEnabled={Boolean(transactionInfo)}
                  copyValue={
                    exchangeType === ExchangeTypeEnum.BUY
                      ? fields?.sum
                      : fields?.calculatedAmount
                  }
                  error={
                    exchangeType === ExchangeTypeEnum.BUY
                      ? errors?.sum?.message
                      : ''
                  }
                  currenciesList={currenciesList}
                  className={styles.smallMargin}
                  currencyId={currencyToId}
                />
              )}
              {!control && (
                <>
                  {currenToAccount &&
                    currentToBalance &&
                    (!overdraftEnabled ||
                      !currenToAccount?.overdraftEnabled ||
                      !currentToBalance?.overdraftEnabled) && (
                      <SidepanelDefaultRow
                        title="Current balance"
                        valueClassName={`${
                          transactionInfo ? styles.readCopyButtonWrapper : ''
                        } ${styles.withMarginBottom}`}
                      >
                        {toAccountBalance !== undefined
                          ? `${formatBalance(
                              toAccountBalance,
                              currenciesList?.find(
                                (item) => item.id === currencyToId,
                              ),
                            )} ${currencyBuyName}`
                          : '-'}
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              toAccountBalance !== undefined
                                ? toAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      </SidepanelDefaultRow>
                    )}
                  {overdraftEnabled &&
                    currenToAccount?.overdraftEnabled &&
                    currentToBalance?.overdraftEnabled && (
                      <>
                        <SidepanelDefaultRow
                          title="Current balance"
                          valueClassName={`${
                            transactionInfo ? styles.readCopyButtonWrapper : ''
                          } ${styles.withMarginBottom}`}
                        >
                          {currentToBalance &&
                          currentToBalance?.balanceWithOverdraft
                            ? `${formatBalance(
                                currentToBalance?.balanceWithOverdraft,
                                currenciesList?.find(
                                  (item) => item.id === currencyFromId,
                                ),
                              )} ${currencySellName}`
                            : '-'}
                          <button
                            type="button"
                            className={`${styles.readCopyButton} ${
                              copyIsSuccess ? styles.success : ''
                            }`}
                            onClick={() =>
                              onCopy(
                                currentToBalance &&
                                  currentToBalance?.balanceWithOverdraft
                                  ? currentToBalance?.balanceWithOverdraft?.toString()
                                  : '-',
                              )
                            }
                            aria-label="Copy"
                          >
                            <IconCopy />
                          </button>
                        </SidepanelDefaultRow>
                        <SidepanelDefaultRow title="Own funds">
                          {currentToBalance
                            ? `${formatBalance(
                                currentToBalance?.value,
                                currenciesList?.find(
                                  (item) => item.id === currencyFromId,
                                ),
                              )} ${currencySellName}`
                            : '-'}
                        </SidepanelDefaultRow>
                        <SidepanelDefaultRow title="Remaining Overdraft">
                          {formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            currentToBalance?.value < 0
                              ? (currentFromBalance?.overdraftValue as number) -
                                  Math.abs(currentToBalance?.value) <
                                0
                                ? 0
                                : (currentToBalance?.overdraftValue as number) -
                                  Math.abs(currentToBalance?.value)
                              : (currentToBalance?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyFromId,
                            ),
                          )}
                        </SidepanelDefaultRow>
                        <SidepanelDefaultRow title="Overdraft Limit">
                          {formatBalance(
                            currentToBalance?.overdraftValue || 0,
                            currenciesList?.find(
                              (item) => item.id === currencyFromId,
                            ),
                          )}
                        </SidepanelDefaultRow>
                      </>
                    )}
                </>
              )}
              {control && (
                <>
                  {balanceToAccountIsLoading && !currentToBalance && (
                    <IconLoader
                      width={12}
                      height={12}
                      className={styles.balanceLoader}
                    />
                  )}
                  {currenToAccount &&
                    currentToBalance &&
                    (!overdraftEnabled ||
                      !currenToAccount?.overdraftEnabled ||
                      !currentToBalance?.overdraftEnabled) && (
                      <div className={styles.currentCurrencyBalance}>
                        Current balance:
                        <span>
                          {!balanceToAccountIsLoading &&
                          toAccountBalance !== undefined
                            ? `${formatBalance(
                                toAccountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyToId,
                                ),
                              )} ${
                                currenciesTo?.find(
                                  (item) => item.value === currencyToId,
                                )?.name
                              }`
                            : '-'}
                        </span>
                      </div>
                    )}

                  {overdraftEnabled &&
                    currenToAccount?.overdraftEnabled &&
                    currentToBalance?.overdraftEnabled && (
                      <div className={styles.exchangeBalance}>
                        <div className={styles.balanceTitle}>
                          Available Funds
                        </div>
                        <ClickOutsideDetector
                          setOptionsListVisible={() => {
                            setBalanceToValueVisible(false);
                            setMaxValueToWidth(0);
                            setBalanceToDetailsOnFocus(false);
                          }}
                        >
                          <span
                            className={styles.balanceValue}
                            onMouseEnter={() => setBalanceToValueVisible(true)}
                          >
                            {!balanceToAccountIsLoading && currentToBalance
                              ? formatBalance(
                                  currentToBalance?.balanceWithOverdraft || 0,
                                  currenciesList?.find(
                                    (item) => item.id === currencyToId,
                                  ),
                                )
                              : ''}
                            {balanceToAccountIsLoading && (
                              <IconLoader
                                width={12}
                                height={12}
                                className={styles.overdraftBalanceLoader}
                              />
                            )}
                            {!balanceToAccountIsLoading && <IconDown />}
                            {!balanceToAccountIsLoading &&
                              currentToBalance &&
                              balanceToValueVisible && (
                                <div
                                  className={styles.balanceDetails}
                                  onMouseEnter={() =>
                                    setBalanceToDetailsOnFocus(true)
                                  }
                                  onMouseLeave={() => {
                                    setBalanceToValueVisible(false);
                                    setMaxValueToWidth(0);
                                    setBalanceToDetailsOnFocus(false);
                                  }}
                                >
                                  <div className={styles.balanceDetailsValue}>
                                    <button
                                      type="button"
                                      className={`${styles.value} value-to`}
                                      style={{
                                        width: maxValueToWidth
                                          ? `${maxValueToWidth}px`
                                          : 'auto',
                                      }}
                                      onClick={() => {
                                        if (
                                          currentToBalance?.value > 0 &&
                                          exchangeType ===
                                            ExchangeTypeEnum.BUY &&
                                          !transactionInfo
                                        ) {
                                          setSumValue(
                                            currentToBalance?.value?.toString(),
                                          );

                                          if (setValue) {
                                            setValue(
                                              'sum',
                                              currentToBalance?.value?.toString(),
                                            );
                                          }
                                        }
                                      }}
                                    >
                                      {formatBalance(
                                        currentToBalance?.value || 0,
                                        currenciesList?.find(
                                          (item) => item.id === currencyFromId,
                                        ),
                                      )}
                                    </button>
                                    <div className={styles.title}>
                                      Own Funds
                                    </div>
                                  </div>
                                  <div className={styles.balanceDetailsValue}>
                                    <button
                                      type="button"
                                      className={`${styles.value} value-to`}
                                      style={{
                                        width: maxValueToWidth
                                          ? `${maxValueToWidth}px`
                                          : 'auto',
                                      }}
                                      onClick={() => {
                                        if (
                                          currentToBalance?.overdraftValue &&
                                          currentToBalance?.overdraftValue >
                                            0 &&
                                          exchangeType ===
                                            ExchangeTypeEnum.BUY &&
                                          !transactionInfo
                                        ) {
                                          setSumValue(
                                            `${
                                              // eslint-disable-next-line no-nested-ternary
                                              currentToBalance?.value < 0
                                                ? (currentFromBalance?.overdraftValue as number) -
                                                    Math.abs(
                                                      currentToBalance?.value,
                                                    ) <
                                                  0
                                                  ? 0
                                                  : (currentToBalance?.overdraftValue ||
                                                      0) -
                                                    Math.abs(
                                                      currentToBalance?.value,
                                                    )
                                                : currentToBalance?.overdraftValue
                                            }`,
                                          );

                                          if (setValue) {
                                            setValue(
                                              'sum',
                                              // eslint-disable-next-line no-nested-ternary
                                              currentToBalance?.value < 0
                                                ? (currentFromBalance?.overdraftValue as number) -
                                                    Math.abs(
                                                      currentToBalance?.value,
                                                    ) <
                                                  0
                                                  ? 0
                                                  : (currentToBalance?.overdraftValue ||
                                                      0) -
                                                    Math.abs(
                                                      currentToBalance?.value,
                                                    )
                                                : currentToBalance?.overdraftValue,
                                            );
                                          }
                                        }
                                      }}
                                    >
                                      {formatBalance(
                                        // eslint-disable-next-line no-nested-ternary
                                        currentToBalance?.value < 0
                                          ? (currentFromBalance?.overdraftValue as number) -
                                              Math.abs(
                                                currentToBalance?.value,
                                              ) <
                                            0
                                            ? 0
                                            : (currentToBalance?.overdraftValue as number) -
                                              Math.abs(currentToBalance?.value)
                                          : (currentToBalance?.overdraftValue as number),
                                        currenciesList?.find(
                                          (item) => item.id === currencyFromId,
                                        ),
                                      )}
                                    </button>
                                    <div className={styles.title}>
                                      Remaining Overdraft
                                    </div>
                                  </div>
                                  <div className={styles.balanceDetailsValue}>
                                    <div
                                      className={`${styles.value} value-to`}
                                      style={{
                                        width: maxValueToWidth
                                          ? `${maxValueToWidth}px`
                                          : 'auto',
                                      }}
                                    >
                                      {formatBalance(
                                        currentToBalance?.overdraftValue || 0,
                                        currenciesList?.find(
                                          (item) => item.id === currencyFromId,
                                        ),
                                      )}
                                    </div>
                                    <div className={styles.title}>
                                      Overdraft Limit
                                    </div>
                                  </div>
                                </div>
                              )}
                          </span>
                        </ClickOutsideDetector>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.transactionBlockWrapper}>
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelNumberRow
                title="Base Rate"
                value={enableEdit ? '' : transactionInfo?.exchangeBaseRate}
                name="baseRate"
                placeholder=""
                control={control}
                enableEdit={enableEdit}
                tabIndex={-1}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? fields?.baseRate
                    : transactionInfo?.exchangeBaseRate
                }
                hintText={`Functional and reporting FX rates, quoted by the source, selected in the PSP settings - ${
                  rateSources?.find((item) => item.value === baseRateSource)
                    ?.name
                }`}
                rightHint
                withHint={Boolean(fields?.baseRate)}
                disabled
              />
              {control && !transactionInfo && (
                <ButtonPseudo
                  icon={<IconRefresh />}
                  onClick={() => onUpdateRates && onUpdateRates()}
                  className={`${styles.updateBalanceButton} ${
                    calculateInProgress ? styles.loading : ''
                  } ${styles.updateRateButton}`}
                  disabled={!providersRate}
                  tabIndex={-1}
                />
              )}
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelNumberRow
                title="Exchange Rate"
                value={enableEdit ? '' : transactionInfo?.exchangeRate}
                name="rate"
                placeholder=""
                control={control}
                enableEdit={enableEdit}
                tabIndex={-1}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit ? fields?.rate : transactionInfo?.exchangeRate
                }
                hintText={`FX rate quoted by the selected FX provider - ${
                  rateSources?.find((item) => item.value === rateSourceValue)
                    ?.name
                }`}
                withHint={Boolean(fields?.rate)}
                disabled
              />
            </div>
          </div>
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelTextRow
                title="Actual Rate"
                value={enableEdit ? '' : transactionInfo?.exchangeActualRate}
                name="exchangeActualRate"
                placeholder=""
                control={control}
                fieldValue={rateValue}
                setFieldValue={setRateValue}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  (!transactionId && !calculateResult && !calcError)
                }
                tabIndex={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED ||
                  (!transactionId && !calculateResult && !calcError)
                    ? -1
                    : 0
                }
                isCreate={!transactionId}
                exchangeFieldType="rate"
                calculateTransactionRatesByIdAction={
                  calculateTransactionRatesByIdAction
                }
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? fields?.exchangeActualRate
                    : transactionInfo?.exchangeActualRate
                }
                hintText="Exchange rate with applied FX margin"
                withHint={Boolean(fields?.exchangeActualRate || rateValue)}
                smallHintWidth
                rightHint
              />
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelTextRow
                title="Inversed Actual Rate"
                value={
                  enableEdit ? '' : transactionInfo?.exchangeInverseActualRate
                }
                name="inverseActualRate"
                placeholder=""
                control={control}
                fieldValue={inversedRateValue}
                setFieldValue={(data) => {
                  setInversedRateValue(data);

                  if (setInverseRateChanged) {
                    setInverseRateChanged(true);
                  }
                }}
                enableEdit={enableEdit}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? fields?.inverseActualRate
                    : transactionInfo?.exchangeInverseActualRate
                }
                calculateTransactionRatesByIdAction={
                  calculateTransactionRatesByIdAction
                }
                exchangeFieldType="inversed-rate"
                disabled={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED ||
                  (!transactionId && !calculateResult && !calcError)
                }
                tabIndex={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED ||
                  (!transactionId && !calculateResult && !calcError)
                    ? -1
                    : 0
                }
              />
            </div>
          </div>
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelAmountRow
                title="Margin, %"
                value={enableEdit ? '' : transactionInfo?.exchangeMarginPercent}
                name="exchangeMarginPercent"
                placeholder=""
                control={control}
                enableEdit={enableEdit}
                fieldValue={marginValue}
                setFieldValue={setMarginValue}
                disabled={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED ||
                  (!transactionId && !calculateResult && !calcError)
                }
                tabIndex={
                  !isEditable ||
                  baseellaStatus === BaseellaStatus.ARCH ||
                  baseellaStatus === BaseellaStatus.REJECTED ||
                  baseellaStatus === BaseellaStatus.AML_REJ ||
                  baseellaStatus === BaseellaStatus.PROC ||
                  baseellaStatus === BaseellaStatus.BOOKED ||
                  baseellaStatus === BaseellaStatus.NOT_SIGNED ||
                  (!transactionId && !calculateResult && !calcError)
                    ? -1
                    : 0
                }
                isCreate={!transactionId}
                exchangeFieldType="margin"
                calculateTransactionRatesByIdAction={
                  calculateTransactionRatesByIdAction
                }
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? `${fields?.exchangeMarginPercent} %`
                    : `${transactionInfo?.exchangeMarginPercent} %`
                }
                withoutFormatting
              />
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelNumberRow
                title="Exchange profit"
                value={enableEdit ? '' : transactionInfo?.exchangeProfit}
                name="exchangeProfit"
                placeholder=""
                control={control}
                enableEdit={enableEdit}
                tabIndex={-1}
                copyEnabled={Boolean(transactionInfo)}
                copyValue={
                  enableEdit
                    ? fields?.exchangeProfit
                    : transactionInfo?.exchangeProfit
                }
                exchangeInverseRate={
                  enableEdit
                    ? fields?.inverseActualRate
                    : transactionInfo?.exchangeInverseActualRate
                }
                exchangeInverseBaseRate={
                  enableEdit
                    ? fields?.inverseBaseRate
                    : transactionInfo?.exchangeInverseBaseRate
                }
                exchangeType={exchangeType}
                accountType={currenFromAccount?.accountType}
                amount={
                  // eslint-disable-next-line no-nested-ternary
                  transactionInfo
                    ? transactionInfo?.sum?.toString()
                    : exchangeType === 'SELL'
                      ? fields?.sellSum?.toString()
                      : fields?.buySum?.toString()
                }
                exchangeRate={
                  enableEdit
                    ? Number(rateValue)
                    : transactionInfo?.exchangeActualRate
                }
                baseRate={
                  enableEdit ? fields?.baseRate : transactionInfo?.baseRate
                }
                disabled
              />
            </div>
          </div>
          <SidepanelNumberRow
            title="Exchange Rate Tolerance, bp"
            value={
              enableEdit
                ? ''
                : transactionInfo?.exchangeRateTolerance?.toString()
            }
            name="exchangeRateTolerance"
            placeholder="Exchange Rate Tolerance"
            control={control}
            enableEdit={enableEdit}
            tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
            disabled={!isEditable || Boolean(transactionInfo)}
            error={errors?.exchangeRateTolerance?.message}
            copyEnabled={Boolean(transactionInfo)}
            copyValue={
              enableEdit
                ? fields?.exchangeRateTolerance
                : transactionInfo?.exchangeRateTolerance
            }
            hintText="Defines the maximum acceptable deviation from the expected execution price for a Fill-or-Kill (FOK) transaction. If the entire transaction cannot be executed immediately within the specified tolerance, the transaction is rejected and no partial execution occurs."
            suffix="bp"
            suffixClassName={styles.toleranceSuffix}
            setError={setError}
            clearErrors={clearErrors}
            withHint
            toleranceRow
          />
          <div className={styles.toleranceHint}>
            From 0.01 to 20 bp.
            <br />
            Leave empty to use the integration default, if set.
          </div>
        </div>

        {(caspEnabled ||
          (!caspEnabled &&
            transactionInfo &&
            transactionInfo.blockchainTransactionHashId)) && (
          <div className={styles.transactionBlockWrapper}>
            <SidepanelTextRow
              title="Blockchain Hash Id"
              value={
                enableEdit ? '' : transactionInfo?.blockchainTransactionHashId
              }
              name="blockchainTransactionHashId"
              placeholder="Enter Blockchain Hash Id"
              control={control}
              enableEdit={enableEdit}
              tabIndex={!isEditable || Boolean(transactionInfo) ? -1 : 0}
              disabled={!isEditable || Boolean(transactionInfo)}
              error={errors?.blockchainTransactionHashId?.message}
              copyEnabled={Boolean(transactionInfo)}
              copyValue={
                enableEdit
                  ? fields?.blockchainTransactionHashId
                  : transactionInfo?.blockchainTransactionHashId
              }
            />
          </div>
        )}
        {!transactionId && control && (
          <DisableCreationOption
            control={control}
            clearErrors={clearErrors}
            withMargin
          />
        )}
      </>
    );
  }

  return content;
}
